import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import buttonDisplayProps from "@insite/mobius/Pagination/buttonDisplayProps";
import Select, { SelectComponentProps } from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import {
    appendLinkElement,
    getCanonical,
    getPrevCurrentNextQueryString,
    hasLinksWithRelPrevNext,
    removeLinkElement,
    updateCanonical,
} from "@insite/mobius/utilities/linkElementsManipulation";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

export interface PaginationPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

export interface ProductListPaginationStyles {
    container?: InjectableCss;
    paginatinLinkStyles?: InjectableCss;
}

export const paginationStyles: ProductListPaginationStyles = {
    container: {
        css: css`
            padding-top: 16px;
            background: linear-gradient(90deg, #266b40 27.08%, #2d804c 71.87%, #43687a 100%);
        `,
    },
    paginatinLinkStyles: {
        css: css`
            cursor: pointer;
            font-family: inherit;
            min-height: 30px;
            line-height: 1;
            backface-visibility: hidden;
            transition: all 200ms ease-in-out 0s;
            word-break: keep-all;
            border-radius: 0.5em;
            background: transparent;
            margin: 0px 2px;
            padding: 0px 8px;
            border: none;
            justify-content: center;
            > span {
                color: rgb(99, 97, 97) !important;
            }
            &[aria-label^="Current"] {
                background: ${getColor("primary")};
                min-height: 0px;
                height: 32px;
                width: 32px;
                padding: 4px !important;
                margin: 4px !important;
                border-radius: 80px !important;
                &:hover {
                    cursor: default;
                    color: white;
                    filter: brightness(150%);
                }
                > span {
                    color: white !important;
                    font-size: 18px;
                    line-height: 24px;
                    font-weight: 600;
                }
            }
        `,
    },
};

const styles = paginationStyles;

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /**
         * Function to build the href for each link in the pagination list. Receives the page index as an argument.
         * Should be built from router/redux location (not `window`) so it works during server-side rendering.
         * Page 1 should return the clean URL with no `page` parameter.
         */
        createHref?: (page: number) => string;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];
        /** String describing the URL of the previous page in the series */
        prevPage?: string;
        /** String describing the the URL of the next page in the series */
        nextPage?: string;
    }
>;

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

const responsiveStyles = css`
    margin: 4px 0;
    width: 100%;
    justify-content: center;
    display: flex;
    align-items: center;
`;

const iconButtonStyles = css`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    width: 24px;
                `,
                null,
                null,
                css`
                    width: 46px;
                `,
                null,
            ],
            "min",
        )};
    justify-content: center;
`;

const numericButtonStyles = css`
    padding: 0 8px;
`;

const PaginationStyle = styled.div<InjectableCss>`
    display: flex;
    align-items: center;
    background: rgb(255, 255, 255);
    min-height: 72px;
    justify-content: flex-start;
    margin: 0px !important;
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    flex-direction: column-reverse;
                `,
                css``,
                css``,
                null,
                null,
            ],
            "max",
        )};
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    display: flex;
    justify-content: flex-end;
    flex-basis: 50%;
    padding-right: 40px;
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    justify-content: center;
                    flex-basis: auto;
                    padding-right: 0px;
                `,
                null,
                null,
                null,
                null,
            ],
            "max",
        )};

    ul {
        list-style: none;
    }
    li {
        display: inline-block;
        button[aria-label^="Current"] {
            background: ${getColor("primary")};
            padding: 4px !important;
            margin: 4px !important;
            border-radius: 80px !important;
            min-height: 0;
            height: 32px;
            width: 32px;

            &:hover {
                cursor: default;
                color: white;
                background-color: rgb(63, 168, 39);
                border-color: rgb(63, 168, 39);
            }
            span {
                font-size: ${TYPOGRAPHY.size.large};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
            }
        }
        a[data-test-selector^="pagination_previousButton"],
        a[data-test-selector^="pagination_firstButton"],
        a[data-test-selector^="pagination_nextButton"],
        a[data-test-selector^="pagination_lastButton"] {
            &:hover {
                color: rgb(99, 97, 97);
                background: rgb(226, 227, 230);
                border-color: rgb(226, 227, 230);
                min-height: 30px;
                border-radius: 0.5em;
            }
        }
    }
    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    padding: 4px 16px;
    > div[class^="FormFieldStyle"] {
        border: 2px solid ${COLORS.background.grayShade};
        border-radius: 40px;
        background: ${COLORS.white} !important;
        width: 220px !important;
        padding: 0;
        label {
            font-size: ${TYPOGRAPHY.size.smaller} !important;
            font-weight: ${TYPOGRAPHY.weight.base} !important;
            line-height: ${TYPOGRAPHY.lineHeight.base} !important;
            color: ${getProp("theme.typography.p.color")} !important;
            padding-top: 7px !important;
        }
        > div[class^="FormInputWrapper"] {
            width: 100% !important;
            select {
                border: none !important;
                border-radius: 40px !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                font-weight: ${TYPOGRAPHY.weight.bold} !important;
                line-height: ${TYPOGRAPHY.lineHeight.base} !important;
                color: ${COLORS.black} !important;
                padding: 0px 7px;
            }
            span {
                color: ${getProp("theme.typography.p.color")} !important;
            }
        }
    }

    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    justify-content: center;
                    flex-basis: auto;
                `,
                css``,
                null,
                null,
                null,
            ],
            "max",
        )};

    ${injectCss}
`;

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const Pagination: React.FC<PaginationProps> = withTheme(props => {
    const {
        createHref,
        currentPage: possibleStringCurrentPage,
        onChangeResultsPerPage,
        onChangePage,
        resultsPerPage: possibleStringResultsPerPage,
        resultsPerPageLabel,
        resultsCount,
        resultsPerPageOptions,
        pageSizeCookie,
        prevPage,
        nextPage,
        ...otherProps
    } = props;

    React.useEffect(() => {
        const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
            prevPage,
            nextPage,
            convertToNumberIfString(possibleStringCurrentPage),
            "",
        );
        const prevCanonical = `${getCanonical()}${prevQueryString}`;
        const nextCanonical = `${getCanonical()}${nextQueryString}`;
        updateCanonical(currentQueryString);

        const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
        const prevNotFromProducts = prevPage?.indexOf("/api/v2/products?") < 0 && prevPage?.indexOf("search=") < 0;
        const nextNotFromProducts = nextPage?.indexOf("/api/v2/products?") < 0 && nextPage?.indexOf("search=") < 0;

        if (prevPage && !hasPrev && prevNotFromProducts) {
            appendLinkElement("prev", prevCanonical);
        }

        if (nextPage && !hasNext && nextNotFromProducts) {
            appendLinkElement("next", nextCanonical);
        }

        return () => {
            const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevPage, nextPage);
            if (prevPage && hasPrev && prevNotFromProducts) {
                removeLinkElement("prev", prevCanonical);
            }

            if (nextPage && hasNext && nextNotFromProducts) {
                removeLinkElement("next", nextCanonical);
            }
        };
    }, [prevPage, nextPage]);

    const currentPage = convertToNumberIfString(possibleStringCurrentPage);
    const resultsPerPage = convertToNumberIfString(possibleStringResultsPerPage);

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        if (pageSizeCookie) {
            setCookie(pageSizeCookie, value);
        }

        onChangeResultsPerPage(event, parseInt(value, 10));
        event.currentTarget.blur();
    };

    const { spreadProps, applyProp } = applyPropBuilder(otherProps, { component: "pagination" });
    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const translate = otherProps.theme.translate;
    const navIconsSrc = spreadProps("navIconsSrc");

    const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;
    let pagesToDisplay: ("e" | number)[] = [];
    if (finalPageIndex < 6) {
        for (let i = 1; i <= finalPageIndex; i += 1) {
            pagesToDisplay.push(i);
        }
    } else if (currentPage < 4) {
        pagesToDisplay = [1, 2, 3, 4, "e", finalPageIndex];
    } else if (currentPage >= 4 && currentPage < finalPageIndex - 2) {
        pagesToDisplay = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, "e", finalPageIndex];
    } else {
        pagesToDisplay = [
            "e",
            finalPageIndex - 4,
            finalPageIndex - 3,
            finalPageIndex - 2,
            finalPageIndex - 1,
            finalPageIndex,
        ];
    }

    /**
     * Builds the href for a page. Prefers the parent-supplied `createHref` (SSR-safe).
     * Falls back to `window.location` on the client only. Page 1 always uses the clean URL (no ?page=1).
     */
    const buildPageHref = (page: number): string => {
        if (createHref) {
            return createHref(page);
        }

        if (typeof window === "undefined" || !window.location) {
            return "";
        }

        const { pathname, search } = window.location;
        const params = new URLSearchParams(search);
        if (page <= 1) {
            params.delete("page");
        } else {
            params.set("page", String(page));
        }
        const query = params.toString();
        return query ? `${pathname}?${query}` : pathname;
    };

    const scrollToTop = () => {
        if (typeof window !== "undefined") {
            window.scrollTo(0, 0);
        }
    };

    /** Lets modified clicks (new tab / new window) fall through to normal browser link behavior. */
    const handlePageClick = (page: number) => (event: React.MouseEvent) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) {
            return;
        }
        event.preventDefault();
        scrollToTop();
        onChangePage(page);
    };

    const paginationButtons = pagesToDisplay.map(page => {
        if (page === "e") {
            return (
                <Typography key={page} css={cssOverrides.ellipsis}>
                    &hellip;
                </Typography>
            );
        }

        return (
            <li key={`page${page}`}>
                <Link
                    {...buttonDisplayProps(props, {
                        moreCss: numericButtonStyles,
                        page,
                        cssOverrides,
                        buttonProps,
                        currentPageButtonVariant,
                    })}
                    href={buildPageHref(page)}
                    onClick={handlePageClick(page)}
                    {...styles.paginatinLinkStyles}
                >
                    {page.toLocaleString("en-US")}
                </Link>
            </li>
        );
    });

    const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
    const backDisabled = currentPage === 1 || finalPageIndex === 1;

    const navIcon = (
        pageIndex: number,
        icon: string | React.ComponentType,
        ariaLabel: string,
        disabledFlag: boolean,
        dataTestSelector?: string,
    ) => {
        const displayProps = buttonDisplayProps(props, {
            page: -1,
            moreCss: iconButtonStyles,
            cssOverrides,
            buttonProps,
            currentPageButtonVariant,
        });

        // Disabled: no valid target, so keep a non-link button
        if (disabledFlag) {
            return (
                <Link
                    variant="secondary"
                    {...displayProps}
                    aria-label={ariaLabel}
                    disabled
                    {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
                >
                    <VisuallyHidden>{ariaLabel}</VisuallyHidden>
                    <ButtonIcon size={15} src={icon} />
                </Link>
            );
        }

        // Enabled: a real <a href>
        return (
            <Link
                {...displayProps}
                aria-label={ariaLabel}
                href={buildPageHref(pageIndex)}
                onClick={handlePageClick(pageIndex)}
                {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
            >
                <VisuallyHidden>{ariaLabel}</VisuallyHidden>
                <ButtonIcon size={15} src={icon} />
            </Link>
        );
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="Pagination_Background">
            <PaginationStyle css={cssOverrides.pagination} {...omitSingle(otherProps, "cssOverrides")}>
                <LinkList
                    role="navigation"
                    aria-label={translate("Pagination navigation")}
                    css={cssOverrides.linkList}
                    data-test-selector={`pagination_currentPage_${currentPage}`}
                >
                    <ul>
                        <li>
                            {navIcon(
                                1,
                                navIconsSrc.firstPage,
                                translate("First page"),
                                backDisabled,
                                "pagination_firstButton",
                            )}
                        </li>
                        <li>
                            {navIcon(
                                currentPage - 1,
                                navIconsSrc.previousPage,
                                translate("Previous page"),
                                backDisabled,
                                "pagination_previousButton",
                            )}
                        </li>
                        {paginationButtons}
                        <li>
                            {navIcon(
                                currentPage + 1,
                                navIconsSrc.nextPage,
                                translate("Next page"),
                                forwardDisabled,
                                "pagination_nextButton",
                            )}
                        </li>
                        <li>
                            {navIcon(
                                finalPageIndex,
                                navIconsSrc.lastPage,
                                translate("Last page"),
                                forwardDisabled,
                                "pagination_lastButton",
                            )}
                        </li>
                    </ul>
                </LinkList>

                {resultsPerPageOptions?.length > 0 && (
                    <PerPageSelect css={cssOverrides.perPageSelect}>
                        <Select
                            onChange={changeResultsPerPage}
                            label={resultsPerPageLabel || translate("Results Per Page")}
                            labelPosition="left"
                            value={resultsPerPage}
                            {...spreadProps("selectProps" as any)}
                            data-test-selector="pagination_resultsPerPage"
                        >
                            {resultsPerPageOptions.map((n: string) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </Select>
                    </PerPageSelect>
                )}
            </PaginationStyle>
        </StyledWrapper>
    );
});

/** @component */
export default Pagination as React.ComponentType<PaginationComponentProps>;

export { Pagination, PaginationStyle };
